/**
 * Edit Content Tool - Factory (with image + write tracking)
 */
import { ToolBase, ToolDescription } from "../../../../../agents/base";
import { buildWriteEntry, collectImagesFromResult, } from "../../../../writeTracking";
import { EditItemSchema, EditContentParamsSchema, } from "./editContent.schema";
const SYSTEM_PROMPT_DESCRIPTION = `### editContent
Edit cell values, formulas, and text in the workbook.

**Range-based editing with auto-fill** (like Excel's fill handle):
\`\`\`json
{
  "edits": [
    {"range": {"startRow": 2, "endRow": 10, "startCol": 2, "endCol": 2}, "content": "=A2*1.1"}
  ]
}
\`\`\`
Formula references auto-adjust: B2 -> =A2*1.1, B3 -> =A3*1.1, etc.

**Reference types for fills:**
- \`A1\` (relative): Adjusts when filling
- \`$A1\` (column fixed): Column stays, row adjusts
- \`A$1\` (row fixed): Row stays, column adjusts
- \`$A$1\` (absolute): Never adjusts

**Content types:** null (clear), "=formula", "text", 123 (number)

**Sheet targeting:** If \`sheetName\` does not exist, editContent creates that worksheet tab and applies edits there.

**Number formats:** Use \`numberFormats\` array with format strings:
ignore, currency, number, percentage, multiple, year, FYyy, mm-yyyy, mm-yy, yyyy, or custom Excel number format string.

**CRITICAL:** No circular references! Never reference a cell in its own formula.
Percentages as decimals: 15% -> 0.15.

**Placement safety rules:**
- Never write to A1 unless the user explicitly requests A1 (or explicitly targets a range that includes A1).
- Never overwrite header labels (typically row 1) unless explicitly requested.
- If placement is ambiguous, read first and then write to an explicit target range.
- Every edit item must include explicit rangeRef or range; never rely on implied/default placement.`;
const STREAM_BATCH_SIZE = 1;
const STREAM_FLUSH_MS = 200;
const STREAM_LOG_PREFIX = "[EditContentStream]";
// Hard toggle for latency A/B testing.
// Set to false to force single-shot editContent calls.
const ENABLE_EDIT_CONTENT_STREAMING = true;
const NUMBER_FORMAT_PRESET_MAP = {
    currency: '_($* #,##0.00_);_($* (#,##0.00);_($* "-"??_);_(@_)',
    number: "#,##0",
    percentage: "0.00%",
    percent: "0.00%",
    multiple: "0.0x",
    year: "0",
    fyyy: '"FY"yy',
    "mm-yyyy": "mm-yyyy",
    "mm-yy": "mm-yy",
    yyyy: "yyyy",
};
function resolveNumberFormat(format) {
    const raw = typeof format === "string" ? format.trim() : "";
    if (!raw)
        return null;
    const key = raw.toLowerCase();
    if (key === "ignore")
        return null;
    if (key === "fy yy" || key === "fy-yy")
        return NUMBER_FORMAT_PRESET_MAP.fyyy;
    const mapped = NUMBER_FORMAT_PRESET_MAP[key];
    if (mapped)
        return mapped;
    // Otherwise assume caller provided a valid Excel number format string.
    return raw;
}
function asObject(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function unwrapRequest(raw) {
    const obj = asObject(raw);
    const nested = obj.request;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        return nested;
    }
    return obj;
}
function resolveSessionId(raw, fallback) {
    const obj = asObject(raw);
    return (typeof obj.sessionId === "string" ? obj.sessionId : undefined) || fallback;
}
export function createEditContentTool(runner, fallbackSessionId) {
    return new EditContentTool(runner, fallbackSessionId).toRuntimeToolDefinition();
}
export class EditContentTool extends ToolBase {
    constructor(runner, fallbackSessionId) {
        super();
        this.runner = runner;
        this.fallbackSessionId = fallbackSessionId;
        this.name = "editContent";
        this.description = new ToolDescription("editContent", "Edit workbook cell content (values, formulas, text, number formats).", EditContentParamsSchema);
        this.paramsSchema = EditContentParamsSchema;
        this.systemPromptDescription = SYSTEM_PROMPT_DESCRIPTION;
        this.fieldsToCheck = [
            { name: "uiDescription", type: "string", list_status: false, order: 0 },
            { name: "sheetName", type: "string", list_status: false, order: 1 },
        ];
        this.listsToCheck = [
            {
                name: "edits",
                type: "array",
                item_type: "object",
                list_status: true,
                order: 2,
                check_sub_field: "content",
            },
        ];
        this.flushQueue = Promise.resolve();
        this.bufferedEdits = [];
        this.streamedRunnerResults = [];
        this.usedStreaming = false;
        this.streamingDisabledWarned = false;
    }
    setSessionId(sessionId) {
        this.fallbackSessionId = sessionId;
    }
    reset() {
        super.reset();
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = undefined;
        }
        this.flushQueue = Promise.resolve();
        this.bufferedEdits = [];
        this.streamedRunnerResults = [];
        this.usedStreaming = false;
        this.streamError = undefined;
        this.cachedSheetName = undefined;
        this.streamingDisabledWarned = false;
    }
    addStep(fieldName, value) {
        super.addStep(fieldName, value);
        if (fieldName === "sheetName" && typeof value === "string") {
            const sheetName = value.trim();
            if (sheetName) {
                this.cachedSheetName = sheetName;
                if (this.bufferedEdits.length > 0) {
                    this.scheduleFlush(0);
                }
            }
            return;
        }
        if (fieldName !== "edits" || !value || typeof value !== "object") {
            return;
        }
        if (!this.isStreamingEnabled()) {
            if (!this.streamingDisabledWarned) {
                console.log(`${STREAM_LOG_PREFIX} Streaming disabled (ENABLE_EDIT_CONTENT_STREAMING=false); ignoring incremental edit items`);
                this.streamingDisabledWarned = true;
            }
            return;
        }
        if (this.streamError) {
            return;
        }
        const parsed = EditItemSchema.safeParse(value);
        if (!parsed.success) {
            console.warn(`${STREAM_LOG_PREFIX} Ignoring invalid streamed edit item`);
            return;
        }
        const edit = parsed.data;
        if (!edit.rangeRef && !edit.range) {
            // Avoid flushing edits that won't resolve to a concrete range.
            console.warn(`${STREAM_LOG_PREFIX} Ignoring streamed edit without range/rangeRef`);
            return;
        }
        this.queueEdit(edit);
    }
    isStreamingEnabled() {
        return ENABLE_EDIT_CONTENT_STREAMING;
    }
    queueEdit(edit) {
        this.usedStreaming = true;
        this.bufferedEdits.push(edit);
        if (this.bufferedEdits.length >= STREAM_BATCH_SIZE) {
            this.scheduleFlush(0);
        }
        else {
            this.scheduleFlush(STREAM_FLUSH_MS);
        }
    }
    scheduleFlush(delayMs) {
        if (this.flushTimer) {
            if (delayMs === 0) {
                clearTimeout(this.flushTimer);
                this.flushTimer = undefined;
            }
            else {
                return;
            }
        }
        if (delayMs === 0) {
            this.flushPendingEdits();
            return;
        }
        this.flushTimer = setTimeout(() => {
            this.flushTimer = undefined;
            this.flushPendingEdits();
        }, delayMs);
    }
    flushPendingEdits() {
        if (this.streamError || this.bufferedEdits.length === 0) {
            return;
        }
        const editsToSend = this.bufferedEdits.splice(0, this.bufferedEdits.length);
        this.flushQueue = this.flushQueue.then(() => this.flushEdits(editsToSend));
    }
    async flushEdits(edits) {
        if (!edits.length || this.streamError) {
            return;
        }
        try {
            const result = await this.runner.editContent({
                sheetName: this.cachedSheetName,
                edits,
            }, { sessionId: this.fallbackSessionId });
            this.streamedRunnerResults.push(result);
            if (!result.success) {
                this.streamError = result.errors?.[0] || "Streaming editContent call failed.";
                console.warn(`${STREAM_LOG_PREFIX} Flush failed: ${this.streamError}`);
            }
        }
        catch (error) {
            this.streamError = error instanceof Error ? error.message : String(error);
            console.warn(`${STREAM_LOG_PREFIX} Flush error: ${this.streamError}`);
        }
    }
    buildStreamingResult() {
        const data = [];
        const warnings = [];
        const errors = [];
        let success = true;
        let durationMs = 0;
        let operationCount = 0;
        let usedBackendFallback = false;
        let timestamp = new Date().toISOString();
        for (const result of this.streamedRunnerResults) {
            success = success && result.success;
            if (Array.isArray(result.data)) {
                data.push(...result.data);
            }
            else if (result.data !== undefined) {
                data.push(result.data);
            }
            if (result.warnings?.length) {
                warnings.push(...result.warnings);
            }
            if (result.errors?.length) {
                errors.push(...result.errors);
            }
            durationMs += result.telemetry?.durationMs ?? 0;
            operationCount += result.telemetry?.operationCount ?? 0;
            usedBackendFallback = usedBackendFallback || Boolean(result.telemetry?.usedBackendFallback);
            if (result.telemetry?.timestamp) {
                timestamp = result.telemetry.timestamp;
            }
        }
        return {
            success,
            data,
            warnings: warnings.length > 0 ? warnings : undefined,
            errors: errors.length > 0 ? errors : undefined,
            telemetry: {
                operationCount: operationCount > 0 ? operationCount : undefined,
                durationMs,
                usedBackendFallback: usedBackendFallback || undefined,
                timestamp,
            },
        };
    }
    attachWriteTracking(result, sheetName) {
        const data = asObject(result.data);
        const effectiveSheet = sheetName || this.cachedSheetName || "Unknown";
        const allImages = [];
        const sheetImages = data.sheet_images_base64;
        const sheetImageNames = data.sheet_image_names;
        if (Array.isArray(sheetImages)) {
            for (let i = 0; i < sheetImages.length; i++) {
                allImages.push({
                    base64: sheetImages[i],
                    label: sheetImageNames?.[i] || effectiveSheet,
                });
            }
        }
        else if (typeof data.sheet_image_base64 === "string") {
            allImages.push({ base64: data.sheet_image_base64, label: effectiveSheet });
        }
        const { imageIds, rawImages } = collectImagesFromResult(allImages, "content_write");
        const excelReadInfos = [];
        if (imageIds.length > 0) {
            for (let i = 0; i < imageIds.length; i++) {
                const label = sheetImageNames?.[i] || effectiveSheet;
                excelReadInfos.push(buildWriteEntry("content", label, "FULL_SHEET", "writeContent", imageIds[i]));
            }
        }
        else {
            excelReadInfos.push(buildWriteEntry("content", effectiveSheet, "FULL_SHEET", "writeContent"));
        }
        return {
            ...result,
            _imageIds: imageIds.length > 0 ? imageIds : undefined,
            _rawImages: rawImages.length > 0 ? rawImages : undefined,
            _excelReadInfos: excelReadInfos.length > 0 ? excelReadInfos : undefined,
        };
    }
    async execute(args) {
        const sessionId = resolveSessionId(args, this.fallbackSessionId);
        const rawRequest = unwrapRequest(args);
        const normalizedNumberFormats = Array.isArray(rawRequest.numberFormats)
            ? rawRequest.numberFormats
                .map((item) => {
                const resolved = resolveNumberFormat(item.format);
                if (!resolved)
                    return null;
                return { ...item, format: resolved };
            })
                .filter(Boolean)
            : undefined;
        const request = {
            ...rawRequest,
            edits: Array.isArray(rawRequest.edits) ? rawRequest.edits : [],
            ...(normalizedNumberFormats && normalizedNumberFormats.length > 0
                ? { numberFormats: normalizedNumberFormats }
                : {}),
        };
        if (typeof request.sheetName === "string" && request.sheetName.trim().length > 0) {
            this.cachedSheetName = request.sheetName.trim();
        }
        try {
            if (this.isStreamingEnabled() && this.usedStreaming) {
                if (this.flushTimer) {
                    clearTimeout(this.flushTimer);
                    this.flushTimer = undefined;
                }
                this.flushPendingEdits();
                await this.flushQueue;
                if (this.streamError) {
                    const partial = this.buildStreamingResult();
                    return this.attachWriteTracking({
                        ...partial,
                        success: false,
                        errors: [...(partial.errors || []), this.streamError],
                    }, request.sheetName);
                }
                if (request.numberFormats && request.numberFormats.length > 0) {
                    const formatResult = await this.runner.editContent({
                        sheetName: request.sheetName ?? this.cachedSheetName,
                        edits: [],
                        numberFormats: request.numberFormats,
                    }, { sessionId });
                    this.streamedRunnerResults.push(formatResult);
                }
                return this.attachWriteTracking(this.buildStreamingResult(), request.sheetName);
            }
            const result = await this.runner.editContent(request, { sessionId });
            const isSheetPreparationOnly = request.edits.length === 0 &&
                (!request.numberFormats || request.numberFormats.length === 0) &&
                typeof request.sheetName === "string" &&
                request.sheetName.trim().length > 0;
            if (!isSheetPreparationOnly) {
                return this.attachWriteTracking(result, request.sheetName);
            }
            return this.attachWriteTracking({
                ...result,
                warnings: [
                    ...(result.warnings || []),
                    "No edits were provided. Sheet was prepared; send a follow-up editContent call with edits[].",
                ],
            }, request.sheetName);
        }
        finally {
            this.reset();
        }
    }
}
